import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { selectRoles } from "../../../../features/roleSlice";
import { getLeadTimes } from "./api";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { PageHeader, PageShell } from "../../../../components/UI/Page";
import { EmptyState, LoadingState } from "../../../../components/UI/States";
import { Pill, inputCls } from "../../../../components/WindowDoor/wdUi";

// "2026-07-20" -> "7/20/2026" (string parse so timezones can't shift the day)
const formatEffectiveDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || "").trim());
  if (!match) return String(value || "").trim();
  return `${Number(match[2])}/${Number(match[3])}/${match[1]}`;
};

const formatTimestamp = (iso) =>
  iso ? new Date(iso).toLocaleDateString() : "";

const itemMatches = (item, query) =>
  !query ||
  item.product.toLowerCase().includes(query) ||
  item.note.toLowerCase().includes(query);

function LeadTimesView() {
  const role = useSelector(selectRoles);
  const [brands, setBrands] = useState(null);
  const [search, setSearch] = useState("");

  const isManager =
    role?.roles?.includes("Window Door Manager") ||
    role?.roles?.includes("Admin");

  useEffect(() => {
    getLeadTimes()
      .then((data) => setBrands(Array.isArray(data) ? data : []))
      .catch(() => setBrands([]));
  }, []);

  // Filter brands/sections/items down to the search text, keeping a section's
  // header match as "show the whole section".
  const visibleBrands = useMemo(() => {
    if (!brands) return null;
    const query = search.trim().toLowerCase();
    if (!query) return brands;
    return brands
      .map((brand) => {
        if (brand.brand.toLowerCase().includes(query)) return brand;
        const sections = (brand.sections || [])
          .map((section) =>
            section.title.toLowerCase().includes(query)
              ? section
              : {
                  ...section,
                  items: (section.items || []).filter((item) =>
                    itemMatches(item, query)
                  ),
                }
          )
          .filter((section) => section.items.length);
        return { ...brand, sections };
      })
      .filter((brand) => (brand.sections || []).length);
  }, [brands, search]);

  return (
    <PageShell size="lg">
      <PageHeader
        icon={ScheduleOutlinedIcon}
        tone="indigo"
        eyebrow="Windows & Doors"
        title="Window & Door Lead Times"
        description="Current supplier lead times by brand. Highlighted rows changed in the latest update; notes are shown in red."
        actions={
          isManager && (
            <Link
              to="/windowdoor/lead-times/manage"
              className="app-btn app-btn-secondary"
            >
              <EditCalendarOutlinedIcon fontSize="small" />
              Manage lead times
            </Link>
          )
        }
      >
        <div className="relative mt-5 w-full max-w-md">
          <SearchIcon
            fontSize="small"
            className="app-muted pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
          />
          <input
            type="search"
            aria-label="Search lead times"
            className={`${inputCls} !pl-10`}
            placeholder="Search products (e.g. patio door, 400 Series)…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </PageHeader>

      <div className="space-y-6">
        {visibleBrands === null && (
          <div className="app-card rounded-2xl border">
            <LoadingState label="Loading lead times…" />
          </div>
        )}

        {visibleBrands !== null && visibleBrands.length === 0 && (
          <div className="app-card rounded-2xl border">
            <EmptyState
              icon={search ? SearchOffIcon : ScheduleOutlinedIcon}
              title={search ? "No matches" : "Nothing published yet"}
              description={
                search
                  ? "No products match your search."
                  : "No lead times have been published yet."
              }
            />
          </div>
        )}

        {visibleBrands?.map((brand) => {
          const changeCount = (brand.sections || []).reduce(
            (count, section) =>
              count +
              (section.items || []).filter(
                (item) => item.was && item.now && item.was !== item.now
              ).length,
            0
          );
          return (
            <section
              key={brand.brandKey}
              className="app-card overflow-hidden rounded-2xl border"
            >
              <div className="flex flex-wrap items-center justify-between gap-2 border-b app-divider px-5 py-4">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="text-lg font-semibold">{brand.brand}</h2>
                  {changeCount > 0 && (
                    <Pill tone="sky">
                      {changeCount} change{changeCount === 1 ? "" : "s"}
                    </Pill>
                  )}
                </div>
                <div className="app-muted text-xs">
                  {brand.effectiveDate &&
                    `Effective ${formatEffectiveDate(brand.effectiveDate)}`}
                  {brand.updatedAt &&
                    ` · Updated ${formatTimestamp(brand.updatedAt)}`}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="app-table">
                  <thead>
                    <tr>
                      <th className="!pl-5">Product</th>
                      <th className="w-28 !text-center sm:w-32">Was</th>
                      <th className="w-28 !pr-5 !text-center sm:w-32">Now</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(brand.sections || []).map((section) => (
                      <React.Fragment key={section.id}>
                        {section.title && (
                          <tr>
                            <td
                              colSpan={3}
                              className="app-eyebrow !bg-[color:var(--app-surface-bg)] !py-2 !pl-5"
                            >
                              {section.title}
                            </td>
                          </tr>
                        )}
                        {(section.items || []).map((item) => {
                          const changed =
                            item.was && item.now && item.was !== item.now;
                          return (
                            <tr
                              key={item.id}
                              className={changed ? "bg-sky-500/10" : ""}
                            >
                              <td className="!pl-5">
                                {item.product}
                                {item.note && (
                                  <div className="mt-0.5 text-xs font-semibold text-red-600 dark:text-red-400">
                                    {item.note}
                                  </div>
                                )}
                              </td>
                              <td className="app-muted text-center tabular-nums">
                                {item.was}
                              </td>
                              <td
                                className={`!pr-5 text-center tabular-nums ${
                                  changed
                                    ? "font-bold text-sky-700 dark:text-sky-300"
                                    : ""
                                }`}
                              >
                                {item.now}
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          );
        })}
      </div>
    </PageShell>
  );
}

export default LeadTimesView;
